// ─────────────────────────────────────────────────────────────────────────────
// v2 primitives — GlassMenu, Segmented, Sheet, Ring, Money, useMedia
// Motion rules (apple-design skill):
//   • springs start from the live value → everything is interruptible
//   • popovers grow out of their trigger (transform-origin = anchor side)
//   • enter & exit along the same path; glass "materialises" (blur + scale)
//   • reduced-motion → springs jump, CSS transitions become cross-fades
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Glyph } from "./icons";
import { Spring, useSpringNumber, createVelocityTracker, project, rubberband } from "./spring";

// ── Formatting ───────────────────────────────────────────────────────────────
export const inr = (v, { decimals = 0 } = {}) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;

export const inrCompact = (v) => {
  const n = Number(v || 0);
  const a = Math.abs(n);
  const s = n < 0 ? "−" : "";
  if (a >= 1e7) return `${s}₹${(a / 1e7).toFixed(a >= 1e8 ? 0 : 1)}Cr`;
  if (a >= 1e5) return `${s}₹${(a / 1e5).toFixed(a >= 1e6 ? 0 : 1)}L`;
  if (a >= 1e3) return `${s}₹${(a / 1e3).toFixed(0)}k`;
  return `${s}₹${a.toFixed(0)}`;
};

// Big money figure that springs to its value; paise set smaller like Apple's pricing.
export function Money({ value, className = "", decimals = true }) {
  const v = useSpringNumber(value);
  const neg = v < 0;
  const abs = Math.abs(v);
  const totalPaise = Math.round(abs * 100); // round once, then split — never shows ".100"
  const whole = Math.floor(totalPaise / 100);
  const paise = totalPaise % 100;
  return (
    <span className={`v2-money ${className}`}>
      {neg && "−"}<span className="v2-money-cur">₹</span>
      {whole.toLocaleString("en-IN")}
      {decimals && <span className="v2-money-dec">.{String(paise).padStart(2, "0")}</span>}
    </span>
  );
}

// ── Media query hook ─────────────────────────────────────────────────────────
export function useMedia(query) {
  const get = () => { try { return window.matchMedia(query).matches; } catch { return false; } };
  const [m, setM] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const h = () => setM(mq.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, [query]);
  return m;
}

// Mount → next frame flip data-state to "open"; on close keep mounted until exit ends.
function usePresence(open, exitMs = 260) {
  const [mounted, setMounted] = useState(open);
  const [state, setState] = useState(open ? "open" : "closed");
  useEffect(() => {
    let raf, t;
    if (open) {
      setMounted(true);
      raf = requestAnimationFrame(() => requestAnimationFrame(() => setState("open")));
    } else {
      setState("closed");
      t = setTimeout(() => setMounted(false), exitMs);
    }
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [open, exitMs]);
  return { mounted, state };
}

// Portal target that carries the v2 tokens (CSS is scoped, portals live outside .v2).
export function Layer({ children }) {
  return createPortal(<div className="v2-layer">{children}</div>, document.body);
}

// ═════════════════════════════════════════════════════════════════════════════
// GlassMenu — frosted popover of pill options; the highlight pill glides
// between items on a critically damped spring (the signature v2 motion).
// items: [{ label, glyph, to?, onSelect?, active?, danger?, hint? } | "divider"]
// ═════════════════════════════════════════════════════════════════════════════
export function GlassMenu({ open, onClose, anchorRef, items, header, placement = "bottom-end", width = 248 }) {
  const { mounted, state } = usePresence(open, 240);
  const menuRef = useRef(null);
  const pillRef = useRef(null);
  const itemRefs = useRef([]);
  const springY = useRef(null);
  const springH = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, origin: "top right" });
  const [hi, setHi] = useState(-1);

  const selectable = items.map((it, i) => (it === "divider" ? null : i)).filter((i) => i !== null);
  const activeIndex = items.findIndex((it) => it && it.active);

  // Position next to the anchor, flipping to stay on screen.
  useLayoutEffect(() => {
    if (!mounted || !anchorRef?.current) return;
    const a = anchorRef.current.getBoundingClientRect();
    const mh = menuRef.current?.offsetHeight || 300;
    const vw = window.innerWidth, vh = window.innerHeight, gap = 10;
    let top, left, origin;
    if (placement === "right-start") {
      // Clear the whole rail, not just the icon inside it.
      const edge = anchorRef.current.closest(".v2-rail")?.getBoundingClientRect().right ?? a.right;
      left = edge + gap + 2;
      top = Math.min(Math.max(12, a.top - 8), vh - mh - 12);
      origin = `left ${Math.max(16, a.top + a.height / 2 - top)}px`;
    } else {
      left = placement === "bottom-start" ? a.left : a.right - width;
      left = Math.min(Math.max(12, left), vw - width - 12);
      top = a.bottom + gap;
      let vert = "top";
      if (top + mh > vh - 12 && a.top - gap - mh > 12) { top = a.top - gap - mh; vert = "bottom"; }
      const ox = Math.min(Math.max(16, a.left + a.width / 2 - left), width - 16);
      origin = `${ox}px ${vert}`;
    }
    setPos({ top, left, origin });
  }, [mounted, anchorRef?.current, placement, width, items.length]); // eslint-disable-line

  // Pill springs (created once per mount).
  useEffect(() => {
    if (!mounted) return;
    const apply = () => {
      if (!pillRef.current) return;
      pillRef.current.style.transform = `translateY(${springY.current.value}px)`;
      pillRef.current.style.height = `${springH.current.value}px`;
    };
    springY.current = new Spring(0, { response: 0.3, onUpdate: apply });
    springH.current = new Spring(0, { response: 0.3, onUpdate: apply });
    const start = activeIndex >= 0 ? activeIndex : -1;
    setHi(start);
    return () => { springY.current.stop(); springH.current.stop(); };
  }, [mounted]); // eslint-disable-line

  // Move pill to the highlighted item. First placement jumps (no slide-in from 0).
  useLayoutEffect(() => {
    const el = itemRefs.current[hi];
    if (!el || !springY.current) return;
    const first = pillRef.current?.dataset.placed !== "1";
    if (first) {
      springY.current.set(el.offsetTop);
      springH.current.set(el.offsetHeight);
      pillRef.current.dataset.placed = "1";
    } else {
      springY.current.to(el.offsetTop);
      springH.current.to(el.offsetHeight);
    }
  }, [hi]);

  // Outside click / Escape / keyboard navigation.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (menuRef.current?.contains(e.target) || anchorRef?.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); anchorRef?.current?.focus?.(); }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setHi((h) => {
          const idx = selectable.indexOf(h);
          const next = e.key === "ArrowDown"
            ? selectable[(idx + 1 + selectable.length) % selectable.length]
            : selectable[(idx - 1 + selectable.length) % selectable.length];
          itemRefs.current[next]?.focus();
          return next;
        });
      }
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onClose);
    };
  }, [open, onClose, anchorRef, selectable.join(",")]); // eslint-disable-line

  if (!mounted) return null;

  return (
    <Layer>
      <div
        ref={menuRef}
        role="menu"
        className="v2-glass-menu"
        data-state={state}
        style={{ top: pos.top, left: pos.left, width, transformOrigin: pos.origin }}
        onPointerLeave={() => setHi(activeIndex)}
      >
        {header && <div className="v2-menu-header">{header}</div>}
        <div className="v2-menu-list">
          <div ref={pillRef} className={`v2-menu-pill${hi >= 0 ? " is-on" : ""}${hi >= 0 && items[hi]?.danger ? " is-danger" : ""}`} />
          {items.map((it, i) => {
            if (it === "divider") return <div key={`d${i}`} className="v2-menu-divider" />;
            const cls = `v2-menu-item${hi === i ? " is-hi" : ""}${it.danger ? " is-danger" : ""}`;
            const inner = (
              <>
                {it.glyph && <Glyph name={it.glyph} size={19} />}
                <span className="v2-menu-label">{it.label}</span>
                {it.hint && <span className="v2-menu-hint">{it.hint}</span>}
                {it.active && <Glyph name="check" size={16} stroke={2} className="v2-menu-check" />}
              </>
            );
            const common = {
              key: it.label,
              ref: (el) => (itemRefs.current[i] = el),
              role: "menuitem",
              className: cls,
              onPointerEnter: () => setHi(i),
              onFocus: () => setHi(i),
            };
            return it.to ? (
              <Link {...common} to={it.to} onClick={() => { it.onSelect?.(); onClose(); }}>{inner}</Link>
            ) : (
              <button {...common} type="button" onClick={() => { it.onSelect?.(); if (!it.keepOpen) onClose(); }}>{inner}</button>
            );
          })}
        </div>
      </div>
    </Layer>
  );
}

// Small helper: a trigger + menu pair.
export function MenuButton({ items, header, placement, width, className = "v2-icon-btn", label = "More", children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <button ref={ref} type="button" className={className} aria-label={label} aria-haspopup="menu"
        aria-expanded={open} data-open={open ? "1" : "0"} onClick={() => setOpen((o) => !o)}>
        {children || <Glyph name="more" size={18} />}
      </button>
      <GlassMenu open={open} onClose={close} anchorRef={ref} items={items} header={header}
        placement={placement} width={width} />
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Segmented control — thumb glides between options on a spring.
// ═════════════════════════════════════════════════════════════════════════════
export function Segmented({ options, value, onChange, size = "md", ariaLabel }) {
  const wrap = useRef(null);
  const thumb = useRef(null);
  const btns = useRef({});
  const sx = useRef(null);
  const sw = useRef(null);

  useEffect(() => {
    const apply = () => {
      if (!thumb.current) return;
      thumb.current.style.transform = `translateX(${sx.current.value}px)`;
      thumb.current.style.width = `${sw.current.value}px`;
    };
    sx.current = new Spring(0, { response: 0.32, onUpdate: apply });
    sw.current = new Spring(0, { response: 0.32, onUpdate: apply });
    return () => { sx.current.stop(); sw.current.stop(); };
  }, []);

  const place = useCallback((animate) => {
    const b = btns.current[value];
    if (!b || !sx.current) return;
    if (animate) { sx.current.to(b.offsetLeft); sw.current.to(b.offsetWidth); }
    else { sx.current.set(b.offsetLeft); sw.current.set(b.offsetWidth); }
  }, [value]);

  const placed = useRef(false);
  useLayoutEffect(() => { place(placed.current); placed.current = true; }, [place]);
  useEffect(() => {
    const ro = new ResizeObserver(() => place(false));
    if (wrap.current) ro.observe(wrap.current);
    return () => ro.disconnect();
  }, [place]);

  return (
    <div ref={wrap} className={`v2-seg v2-seg--${size}`} role="tablist" aria-label={ariaLabel}>
      <span ref={thumb} className="v2-seg-thumb" />
      {options.map((o) => (
        <button key={o.value} ref={(el) => (btns.current[o.value] = el)} type="button" role="tab"
          aria-selected={value === o.value} className={`v2-seg-btn${value === o.value ? " is-on" : ""}`}
          onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Ring — progress ring whose arc springs to its value.
// ═════════════════════════════════════════════════════════════════════════════
export function Ring({ value = 0, size = 72, stroke = 8, color = "var(--v2-accent)", children, label }) {
  const v = useSpringNumber(Math.max(0, Math.min(100, value)), { response: 1 });
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="v2-ring" style={{ width: size, height: size }} role="img" aria-label={label}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--v2-fill)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - v / 100)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </svg>
      <div className="v2-ring-center">{children}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Sheet — modal surface. Desktop: dialog that grows from its trigger.
// Phone: bottom sheet you can drag down; release velocity is projected
// (Apple's momentum formula) and handed to the spring — no seam.
// ═════════════════════════════════════════════════════════════════════════════
export function Sheet({ open, onClose, title, subtitle, children, footer, originRef, width = 480 }) {
  const isPhone = useMedia("(max-width: 640px)");
  const { mounted, state } = usePresence(open, isPhone ? 0 : 280);
  const panel = useRef(null);
  const scrim = useRef(null);
  const ySpring = useRef(null);
  const drag = useRef(null);
  const [origin, setOrigin] = useState("50% 50%");
  const [phoneMounted, setPhoneMounted] = useState(false);

  // Desktop: grow from the trigger.
  useLayoutEffect(() => {
    if (!open || isPhone || !originRef?.current || !panel.current) return;
    const a = originRef.current.getBoundingClientRect();
    const p = panel.current.getBoundingClientRect();
    setOrigin(`${a.left + a.width / 2 - p.left}px ${a.top + a.height / 2 - p.top}px`);
  }, [open, isPhone, mounted, originRef]);

  // Phone: spring-driven translateY.
  const applyY = useCallback((y) => {
    if (!panel.current) return;
    const h = panel.current.offsetHeight || 1;
    panel.current.style.transform = `translateY(${y}px)`;
    if (scrim.current) scrim.current.style.opacity = String(Math.max(0, Math.min(1, 1 - y / h)));
  }, []);

  useEffect(() => {
    if (!isPhone) return;
    if (open) setPhoneMounted(true);
  }, [open, isPhone]);

  useLayoutEffect(() => {
    if (!isPhone || !phoneMounted || !panel.current) return;
    const h = panel.current.offsetHeight;
    if (!ySpring.current) {
      ySpring.current = new Spring(h, { response: 0.36, onUpdate: applyY });
      applyY(h);
    }
    if (open) ySpring.current.to(0, { dampingRatio: 1 });
    else {
      ySpring.current.onRest = () => { setPhoneMounted(false); ySpring.current = null; };
      ySpring.current.to(panel.current.offsetHeight, { dampingRatio: 1 });
    }
  }, [open, isPhone, phoneMounted, applyY]);

  // Escape + body scroll lock.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  // Focus first field when opened.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => panel.current?.querySelector("input, select, textarea, button")?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  const onPointerDown = (e) => {
    if (!isPhone || !ySpring.current) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const tracker = createVelocityTracker();
    drag.current = { startY: e.clientY, base: ySpring.current.value, tracker };
    ySpring.current.stop();
    tracker.add(e.clientY);
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    d.tracker.add(e.clientY);
    let y = d.base + (e.clientY - d.startY);
    if (y < 0) y = rubberband(y, panel.current.offsetHeight); // resist upward
    ySpring.current.set(y);
  };
  const onPointerUp = () => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    const v = d.tracker.velocity();
    const h = panel.current.offsetHeight;
    const projected = ySpring.current.value + project(v);
    if (projected > h * 0.5) {
      ySpring.current.velocity = v; // hand the flick's speed to the dismiss spring — no seam
      onClose();
    }
    else ySpring.current.to(0, { velocity: v, dampingRatio: 0.85 }); // flick carried momentum → slight bounce
  };

  if (isPhone ? !phoneMounted : !mounted) return null;

  const dragProps = isPhone ? { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp } : {};

  return (
    <Layer>
      <div className={`v2-sheet-root${isPhone ? " is-phone" : ""}`} data-state={isPhone ? "open" : state}>
        <div ref={scrim} className="v2-scrim" onClick={onClose} />
        <div ref={panel} className="v2-sheet" role="dialog" aria-modal="true" aria-label={title}
          style={isPhone ? undefined : { maxWidth: width, transformOrigin: origin }}>
          <div className="v2-sheet-head" {...dragProps}>
            {isPhone && <span className="v2-grabber" />}
            <div className="v2-sheet-titles">
              <div className="v2-sheet-title">{title}</div>
              {subtitle && <div className="v2-sheet-sub">{subtitle}</div>}
            </div>
            <button type="button" className="v2-icon-btn v2-icon-btn--soft" aria-label="Close" onClick={onClose}>
              <Glyph name="close" size={16} stroke={2} />
            </button>
          </div>
          <div className="v2-sheet-body">{children}</div>
          {footer && <div className="v2-sheet-foot">{footer}</div>}
        </div>
      </div>
    </Layer>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Switch — iOS toggle.
// ═════════════════════════════════════════════════════════════════════════════
export function Switch({ checked, onChange, label, description, id }) {
  return (
    <label className="v2-switch-row" htmlFor={id}>
      <span className="v2-switch-text">
        <span className="v2-switch-label">{label}</span>
        {description && <span className="v2-switch-desc">{description}</span>}
      </span>
      <input id={id} type="checkbox" role="switch" className="v2-switch" checked={checked}
        onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

// Skeleton block
export const Skel = ({ h = 16, w = "100%", r = 8, style }) => (
  <span className="v2-skel" style={{ height: h, width: w, borderRadius: r, ...style }} />
);
